import { articleFaqNode } from '@/seo/articleFaqs';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import {
  SITE_URL,
  buildOrganizationNode,
  buildPersonSchema,
  getAuthor,
  getAuthorUrl,
} from '@/config/authors';

interface BlogArticleSEOProps {
  title: string;
  description: string;
  slug: string;
  slugEl?: string;
  slugEn?: string;
  publishedAt: string | null;
  updatedAt?: string | null;
  /** Slug of the author in the author registry. */
  authorSlug?: string;
  image?: string;
  keywords?: string[];
  readingTime?: number;
  category?: string;
  /** Plain-text body of the article (HTML stripped by the caller or here). */
  articleBody?: string;
  wordCount?: number;
}

const stripHtml = (html: string) =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

export const BlogArticleSEO = ({
  title,
  description,
  slug,
  slugEl,
  slugEn,
  publishedAt,
  updatedAt,
  authorSlug,
  image,
  keywords = [],
  readingTime,
  category,
  articleBody,
  wordCount,
}: BlogArticleSEOProps) => {
  const { language } = useLanguage();
  const isEl = language === 'el';
  const author = getAuthor(authorSlug);

  // English-only public site: clean URLs without /en/ or /el/ prefix.
  const canonicalUrl = `${SITE_URL}/blog/${slug}`;
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://scluxxccawjbmeelfdwr.supabase.co';
  const ogImageUrl = `${supabaseUrl}/functions/v1/og-article-image?slug=${encodeURIComponent(slug)}&lang=${language}`;
  const finalImage = image || ogImageUrl;

  const plainBody = articleBody ? stripHtml(articleBody) : '';
  const finalWordCount =
    wordCount ?? (plainBody ? plainBody.split(/\s+/).filter(Boolean).length : undefined);

  const blogPosting: Record<string, unknown> = {
    "@type": "BlogPosting",
    "@id": `${canonicalUrl}#article`,
    headline: title,
    name: title,
    description,
    image: finalImage,
    datePublished: publishedAt,
    dateModified: updatedAt || publishedAt,
    author: { "@id": author.id },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonicalUrl,
    },
    url: canonicalUrl,
    articleSection: category || 'CRO',
    keywords: keywords.join(', '),
    inLanguage: isEl ? 'el-GR' : 'en-US',
  };

  if (plainBody) blogPosting.articleBody = plainBody;
  if (finalWordCount) blogPosting.wordCount = finalWordCount;
  if (readingTime) blogPosting.timeRequired = `PT${readingTime}M`;

  const breadcrumb = {
    "@type": "BreadcrumbList",
    "@id": `${canonicalUrl}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: isEl ? 'Αρχική' : 'Home', item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: 'Blog', item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: title, item: canonicalUrl },
    ],
  };

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      blogPosting,
      buildPersonSchema(author, isEl),
      buildOrganizationNode(),
      breadcrumb,
      ...[articleFaqNode(canonicalUrl, articleBody)].filter(Boolean),
    ],
  };

  // The Netlify edge function server-injects the same @graph into <head>.
  // Skip the client-side copy when it is already there to avoid duplicates.
  const hasServerSchema =
    typeof document !== 'undefined' && !!document.querySelector('script[data-ssr-schema]');

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={language} />
      <title>{title.length > 47 ? title : `${title} | EyeCaptain`}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      <meta name="author" content={author.name} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang — English-only public site */}
      <link rel="alternate" hrefLang="en" href={canonicalUrl} />
      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="article" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={finalImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="EyeCaptain" />
      <meta property="og:locale" content="en_US" />
      {publishedAt && <meta property="article:published_time" content={publishedAt} />}
      {updatedAt && <meta property="article:modified_time" content={updatedAt} />}
      <meta property="article:author" content={getAuthorUrl(author.slug)} />
      {category && <meta property="article:section" content={category} />}
      {keywords.map((kw, i) => (
        <meta key={i} property="article:tag" content={kw} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={finalImage} />
      {readingTime && <meta name="twitter:label1" content="Reading time" />}
      {readingTime && <meta name="twitter:data1" content={`${readingTime} min read`} />}
      {finalWordCount && <meta name="twitter:label2" content="Words" />}
      {finalWordCount && <meta name="twitter:data2" content={`${finalWordCount}`} />}

      {/* BlogPosting + Person + Organization + BreadcrumbList */}
      {!hasServerSchema && (
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      )}
    </Helmet>
  );
};

export default BlogArticleSEO;
